import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { unstable_useBlocker as useBlocker } from "react-router-dom";

import usePlanner from "features/planner/state/usePlanner";
import { shouldBypassUnsavedChangesGuard } from "features/planner/state/navigationGuard";
import { isDemoMode } from "shared/config/appConfig";
import DemoModeDialog from "shared/ui/DemoModeDialog";
import logger from "utils/logger";
import ConfirmUnsavedChangesDialog from "./ConfirmUnsavedChangesDialog";

const getProgramIdentity = (pathname) => {
  const match = pathname.match(/^\/planner\/([^/]+)\/?$/);
  return match ? match[1] : null;
};

function UnsavedChangesGuard({ onInvalidProgram }) {
  const { discardCurrentAutosave, hasUnsavedChanges, isProgramValid, saveProgram } = usePlanner();
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [isDemoDialogOpen, setIsDemoDialogOpen] = useState(false);

  const shouldBlock = useCallback(
    ({ currentLocation, nextLocation }) => {
      if (!hasUnsavedChanges || shouldBypassUnsavedChangesGuard(nextLocation)) return false;

      return (
        getProgramIdentity(currentLocation.pathname) !== getProgramIdentity(nextLocation.pathname)
      );
    },
    [hasUnsavedChanges]
  );

  const blocker = useBlocker(shouldBlock);

  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (blocker.state === "unblocked") {
      setSaveError("");
      setIsSaving(false);
    }
  }, [blocker.state]);

  const handleCancel = () => {
    if (blocker.state !== "blocked") return;

    setSaveError("");
    blocker.reset();
  };

  const handleDiscard = () => {
    if (blocker.state !== "blocked") return;

    discardCurrentAutosave();
    blocker.proceed();
  };

  const handleSaveAndContinue = async () => {
    if (blocker.state !== "blocked") return;

    if (isDemoMode) {
      setIsDemoDialogOpen(true);
      return;
    }

    if (!isProgramValid) {
      blocker.reset();
      onInvalidProgram();
      return;
    }

    setIsSaving(true);
    setSaveError("");

    try {
      await saveProgram({ navigateAfterCreate: false });
      setIsSaving(false);
      blocker.proceed();
    } catch (err) {
      logger.error("Error saving program before navigation", err);
      setSaveError("No se pudo guardar la rutina. Revisa los datos e inténtalo de nuevo.");
      setIsSaving(false);
    }
  };

  return (
    <>
      <ConfirmUnsavedChangesDialog
        open={blocker.state === "blocked"}
        isSaving={isSaving}
        errorMessage={saveError}
        onSaveAndContinue={handleSaveAndContinue}
        onDiscard={handleDiscard}
        onCancel={handleCancel}
      />
      <DemoModeDialog open={isDemoDialogOpen} onClose={() => setIsDemoDialogOpen(false)} />
    </>
  );
}

UnsavedChangesGuard.propTypes = {
  onInvalidProgram: PropTypes.func.isRequired,
};

export default UnsavedChangesGuard;
